import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, ChevronRight, RefreshCw } from 'lucide-react';
import api from '../api';
import { EmptyState, ListSkeleton } from '../components/ui';
import { useIsMobile } from '../hooks/useResponsive';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import { FormatTag, PubPill, Thumb, giornoLabel, hhmm, todayStr } from '../components/content';

/**
 * Pubblicazioni: tutto ciò che esce (o è uscito) sui social, in ordine di tempo.
 * "In arrivo" dal più vicino; "Già usciti" dal più recente. Errori sempre in cima.
 */
const FILTRI = [
  { k: '', label: 'Tutti' },
  { k: 'failed', label: 'Errori' },
  { k: 'scheduled', label: 'Programmati' },
  { k: 'none', label: 'Da pubblicare' },
  { k: 'published', label: 'Pubblicati' },
];

const statoDi = (r) => {
  const s = r.pub_stato || 'none';
  return s === 'processing' || s === 'pending' ? 'scheduled' : s;
};

function fmtNum(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n >= 10000 ? `${(n / 1000).toFixed(1).replace('.', ',')}k` : n.toLocaleString('it-IT');
}

function Riga({ r, onOpen, isMobile }) {
  const s = statoDi(r);
  const insights = r.insights && typeof r.insights === 'object'
    ? Object.entries(r.insights).filter(([k, v]) => k !== 'on' && v !== null && v !== '').slice(0, 3) : [];
  const ora = r.schedule_at
    ? new Date(r.schedule_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    : hhmm(r.ora_pubblicazione);
  return (
    <div className="eden-list-row" onClick={() => onOpen(r)} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onOpen(r); }}
      style={s === 'failed' ? { boxShadow: 'inset 3px 0 0 var(--red)' } : undefined}>
      <Thumb r={r} size={isMobile ? 52 : 46} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.titolo_post}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-dim)' }}>
          <FormatTag formato={r.formato} />
          <span>{r.cliente}</span>
          {r.piattaforma && !isMobile && <span>· {r.piattaforma}</span>}
          {ora && <span style={{ fontVariantNumeric: 'tabular-nums' }}>· {ora}</span>}
          {isMobile && <PubPill stato={s} />}
        </div>
        {s === 'failed' && r.pub_error && (
          <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>{r.pub_error}</div>
        )}
        {insights.length > 0 && (
          <div style={{ display: 'flex', gap: 12, marginTop: 5, fontSize: 12, color: 'var(--text-muted)' }}>
            {insights.map(([k, v]) => (
              <span key={k}><b style={{ color: 'var(--text)', fontWeight: 650 }}>{fmtNum(v)}</b> {k.replace(/_/g, ' ')}</span>
            ))}
          </div>
        )}
      </div>
      {!isMobile && <PubPill stato={s} />}
      <ChevronRight size={16} color="var(--text-dim)" style={{ flexShrink: 0 }} />
    </div>
  );
}

export default function Pubblicazioni() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [cliente, setCliente] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback((silenzioso = false) => {
    if (!silenzioso) setLoading(true);
    api.get('/pubblicazioni', { silent: silenzioso }).then(r => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(true), 60000);

  const clienti = useMemo(() => {
    const m = new Map();
    rows.forEach(r => { if (r.cliente_id) m.set(r.cliente_id, r.cliente || 'Cliente'); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  // Filtri cliente + ricerca (i conteggi dei chip rispettano questi due filtri)
  const base = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (cliente && r.cliente_id !== cliente) return false;
      if (needle && !`${r.titolo_post || ''} ${r.id_post || ''} ${r.cliente || ''} ${r.formato || ''}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [rows, cliente, q]);

  const counts = useMemo(() => {
    const c = { '': base.length, failed: 0, scheduled: 0, none: 0, published: 0 };
    base.forEach(r => { c[statoDi(r)] = (c[statoDi(r)] || 0) + 1; });
    return c;
  }, [base]);

  const visibili = useMemo(() => (filtro ? base.filter(r => statoDi(r) === filtro) : base), [base, filtro]);

  // Raggruppa per giorno: prima gli errori, poi in arrivo (crescente), poi già usciti (decrescente)
  const sezioni = useMemo(() => {
    const oggi = todayStr();
    const errori = visibili.filter(r => statoDi(r) === 'failed');
    const resto = visibili.filter(r => statoDi(r) !== 'failed');
    const futuri = resto.filter(r => !r.data_pubblicazione || String(r.data_pubblicazione).slice(0, 10) >= oggi);
    const passati = resto.filter(r => r.data_pubblicazione && String(r.data_pubblicazione).slice(0, 10) < oggi);
    const perGiorno = (list, asc) => {
      const m = new Map();
      [...list].sort((a, b) => {
        const x = String(a.data_pubblicazione || '9999'), y = String(b.data_pubblicazione || '9999');
        return asc ? x.localeCompare(y) : y.localeCompare(x);
      }).forEach(r => {
        const k = r.data_pubblicazione ? String(r.data_pubblicazione).slice(0, 10) : '';
        if (!m.has(k)) m.set(k, []);
        m.get(k).push(r);
      });
      return [...m.entries()];
    };
    return [
      ...(errori.length ? [{ key: 'err', titolo: 'Da sistemare', giorni: [['__err', errori]] }] : []),
      ...(futuri.length ? [{ key: 'fut', titolo: 'In arrivo', giorni: perGiorno(futuri, true) }] : []),
      ...(passati.length ? [{ key: 'pas', titolo: 'Già usciti', giorni: perGiorno(passati, false) }] : []),
    ];
  }, [visibili]);

  const open = (r) => navigate(`/ped/contenuto/${r.id}`);

  return (
    <div className="eden-page">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0 }}>Pubblicazioni</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Cosa esce sui social e com'è andata.
          </div>
        </div>
        <button className="eden-btn" onClick={() => load()} aria-label="Aggiorna"><RefreshCw size={15} />{!isMobile && 'Aggiorna'}</button>
      </div>

      {/* Filtri */}
      <div className="eden-toolbar">
        <div className="eden-field-search" style={{ flex: isMobile ? '1 1 100%' : '1 1 260px' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          <input className="eden-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca titolo, codice, cliente…" aria-label="Cerca" />
        </div>
        {clienti.length > 1 && (
          <select className="eden-select" value={cliente} onChange={e => setCliente(e.target.value)} aria-label="Filtra per cliente"
            style={{ height: 36, padding: '0 10px', fontSize: 13, flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
            <option value="">Tutti i clienti</option>
            {clienti.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
          </select>
        )}
      </div>
      <div className="eden-scroll-row" style={{ marginBottom: 16 }}>
        {FILTRI.map(f => {
          const n = counts[f.k] || 0;
          if (f.k === 'failed' && n === 0) return null;
          return (
            <button key={f.k || 'all'} className={`eden-chip${filtro === f.k ? ' is-on' : ''}`} onClick={() => setFiltro(f.k)}
              style={f.k === 'failed' ? { color: 'var(--red)', borderColor: filtro === f.k ? 'var(--red)' : 'var(--border)', background: filtro === f.k ? 'var(--red-dim)' : undefined } : undefined}>
              {f.label} <span className="eden-chip-n">{n}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <ListSkeleton rows={6} />
      ) : visibili.length === 0 ? (
        <EmptyState icon={Send}
          title={rows.length ? 'Nessun post con questi filtri' : 'Nessun post'}
          hint={rows.length ? 'Prova a cambiare filtro o ricerca.' : 'Quando crei contenuti nel Piano Editoriale compaiono qui con il loro stato di pubblicazione.'} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {sezioni.map(sez => (
            <section key={sez.key}>
              <h2 style={{ fontSize: 13, fontWeight: 700, color: sez.key === 'err' ? 'var(--red)' : 'var(--text-muted)', margin: '0 0 8px 2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {sez.titolo}
              </h2>
              <div className="eden-list">
                {sez.giorni.map(([giorno, list]) => (
                  <React.Fragment key={giorno || 'nodate'}>
                    {giorno !== '__err' && (
                      <div className="eden-list-head" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12.5, color: giorno === todayStr() ? 'var(--cyan)' : 'var(--text-muted)' }}>
                        <span style={{ textTransform: 'capitalize' }}>{giorno ? giornoLabel(giorno, { long: true }) : 'Senza data'}</span>
                        <span style={{ color: 'var(--text-dim)', fontWeight: 500 }}> · {list.length}</span>
                      </div>
                    )}
                    {list.map(r => <Riga key={r.id} r={r} onOpen={open} isMobile={isMobile} />)}
                  </React.Fragment>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
